import { ApiError, apiRequest } from '../api/client';
import { fetchAllPages } from '../api/fetch-all-pages';
import { useSessionStore } from '../auth/session-store';
import {
  type DeliveryNote,
  type DeliveryNoteSearchParams,
  type DeliveryNoteSearchResult,
  type DeliveryNoteStatus,
} from '../delivery-notes/api';
import { getOfflineStore, type OfflineStore } from '../offline/offline-store';
import {
  createSignatureQueue,
  isDefiniteRejection,
  withTimeout,
  type SignatureCapture,
  type SignatureQueue,
  type SyncSummary,
} from './signature-queue';

/**
 * The single seam between the "Mis entregas" screens and the network: reads
 * fall back to the last data stored on the phone, and signatures go through
 * a durable queue before they are sent.
 */

export { isDefiniteRejection };
export type { SignatureCapture };

/** What a phone still has to deliver: the statuses the screen lists, in display order. */
export const DELIVERABLE_STATUSES: readonly DeliveryNoteStatus[] = ['DISPATCHED', 'DELIVERED'];

const PAGE_SIZE = 100;

/** A read that has not answered by now is treated like no signal, so the phone falls back to what it stored. */
const DEFAULT_READ_TIMEOUT_MS = 10_000;

export interface DeliveriesSnapshot<T> {
  data: T;
  /** ISO instant the data came from the server, shown as "Datos actualizados a las HH:MM". */
  fetchedAt: string;
  /** True when the server could not be reached and `data` is what the phone had stored. */
  fromCache: boolean;
}

export type SubmitResult =
  | { kind: 'synced'; note: DeliveryNote }
  /** Stored on the phone; it reaches the server when there is signal. */
  | { kind: 'queued' }
  /** The server refused it (voided / signed elsewhere); the item stays on the card until dismissed. */
  | { kind: 'problem'; message: string };

interface CachedSnapshot<T> {
  data: T;
  fetchedAt: string;
}

export interface DeliveriesApi {
  listPage: (params: DeliveryNoteSearchParams, signal?: AbortSignal) => Promise<DeliveryNoteSearchResult>;
  getNote: (id: string, signal?: AbortSignal) => Promise<DeliveryNote>;
  sign: (noteId: string, capture: SignatureCapture, signal?: AbortSignal) => Promise<DeliveryNote>;
}

export interface DeliveriesRepositoryDeps {
  store: OfflineStore;
  queue: SignatureQueue;
  api: DeliveriesApi;
  getUserId: () => string | null;
  now?: () => Date;
  readTimeoutMs?: number;
}

/** No usable answer: no signal, DNS, a timeout, or a server that is down (5xx). A 4xx is an answer and is never masked. */
function shouldFallBack(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

export function createDeliveriesRepository({
  store,
  queue,
  api,
  getUserId,
  now = () => new Date(),
  readTimeoutMs = DEFAULT_READ_TIMEOUT_MS,
}: DeliveriesRepositoryDeps) {
  const listKey = (userId: string, date: string): string => `${userId}:list:${date}`;
  const detailKey = (userId: string, id: string): string => `${userId}:detail:${id}`;

  /** A cache that cannot be written must not break a screen that just loaded fine. */
  const remember = async (key: string, value: CachedSnapshot<unknown>): Promise<void> => {
    try {
      await store.put('cache', key, value);
    } catch {
      // Best effort only.
    }
  };

  const recall = async <T>(key: string | null): Promise<DeliveriesSnapshot<T> | null> => {
    if (key === null) {
      return null;
    }

    try {
      const cached = await store.get<CachedSnapshot<T>>('cache', key);

      return cached === undefined ? null : { ...cached, fromCache: true };
    } catch {
      return null;
    }
  };

  const patchCachedNote = async (userId: string, note: DeliveryNote): Promise<void> => {
    try {
      const existing = await store.get<CachedSnapshot<DeliveryNote>>('cache', detailKey(userId, note.id));

      await remember(detailKey(userId, note.id), {
        data: note,
        fetchedAt: existing?.fetchedAt ?? now().toISOString(),
      });

      for (const [key, value] of await store.entries<CachedSnapshot<DeliveryNote[]>>('cache')) {
        if (key.startsWith(`${userId}:list:`) && value.data.some((candidate) => candidate.id === note.id)) {
          await remember(key, {
            ...value,
            data: value.data.map((candidate) => (candidate.id === note.id ? note : candidate)),
          });
        }
      }
    } catch {
      // The server already has the signature; a stale local copy only lasts until the next online load.
    }
  };

  const syncPending = async (userId: string): Promise<SyncSummary> => {
    const summary = await queue.syncAll(userId);

    for (const note of summary.synced) {
      await patchCachedNote(userId, note);
    }

    return summary;
  };

  return {
    syncPending,

    /** Notes of the user's scope for one service date. The API already applies the operating-unit scope. */
    async loadDeliveries(serviceDate: string): Promise<DeliveriesSnapshot<DeliveryNote[]>> {
      const userId = getUserId();
      const key = userId === null ? null : listKey(userId, serviceDate);

      try {
        if (isOffline()) {
          throw new TypeError('offline');
        }

        // One timeout for the whole walk over every page and status; it aborts whatever is still in flight.
        const perStatus = await withTimeout(
          (signal) =>
            Promise.all(
              DELIVERABLE_STATUSES.map((status) =>
                fetchAllPages<DeliveryNote>(
                  (page, pageSize) =>
                    api.listPage(
                      {
                        serviceDateFrom: serviceDate,
                        serviceDateTo: serviceDate,
                        status,
                        page,
                        pageSize,
                      },
                      signal,
                    ),
                  PAGE_SIZE,
                ),
              ),
            ),
          readTimeoutMs,
        );
        const snapshot = { data: perStatus.flat(), fetchedAt: now().toISOString() };

        if (userId !== null && key !== null) {
          await remember(key, snapshot);
          // So a card opened without signal still has its detail.
          await Promise.all(
            snapshot.data.map((note) =>
              remember(detailKey(userId, note.id), { data: note, fetchedAt: snapshot.fetchedAt }),
            ),
          );
        }

        return { ...snapshot, fromCache: false };
      } catch (error) {
        const cached = shouldFallBack(error) ? await recall<DeliveryNote[]>(key) : null;

        if (cached === null) {
          throw error;
        }

        return cached;
      }
    },

    async loadDelivery(id: string): Promise<DeliveriesSnapshot<DeliveryNote>> {
      const userId = getUserId();
      const key = userId === null ? null : detailKey(userId, id);

      try {
        if (isOffline()) {
          throw new TypeError('offline');
        }

        const snapshot = { data: await withTimeout((signal) => api.getNote(id, signal), readTimeoutMs), fetchedAt: now().toISOString() };

        if (key !== null) {
          await remember(key, snapshot);
        }

        return { ...snapshot, fromCache: false };
      } catch (error) {
        const cached = shouldFallBack(error) ? await recall<DeliveryNote>(key) : null;

        if (cached === null) {
          throw error;
        }

        return cached;
      }
    },

    /**
     * Writes the capture to the phone first, then tries to send it. Only a
     * definite validation error is thrown (the person is looking at the
     * modal and can redraw); everything else stays safely queued.
     */
    async submitSignature(noteId: string, capture: SignatureCapture): Promise<SubmitResult> {
      const userId = getUserId();

      if (userId === null) {
        throw new Error('A signature needs a logged in user');
      }

      const item = await queue.enqueue(userId, noteId, capture);
      const result = await queue.attempt(item);

      if (result.kind === 'synced') {
        await patchCachedNote(userId, result.note);

        return { kind: 'synced', note: result.note };
      }

      if (result.kind === 'problem') {
        if (result.item.problem?.code === 'REJECTED') {
          await queue.remove(userId, noteId);

          throw result.error;
        }

        return { kind: 'problem', message: result.item.problem?.message ?? '' };
      }

      return { kind: 'queued' };
    },
  };
}

export type DeliveriesRepository = ReturnType<typeof createDeliveriesRepository>;

const currentUserId = (): string | null => useSessionStore.getState().session?.userId ?? null;

/**
 * The same endpoints as `delivery-notes/api`, with a signal so a timeout can
 * cancel the request (that module's helpers do not take one).
 */
const defaultApi: DeliveriesApi = {
  listPage: (params, signal) => {
    const query = new URLSearchParams();

    if (params.serviceDateFrom !== undefined) {
      query.set('serviceDateFrom', params.serviceDateFrom);
    }

    if (params.serviceDateTo !== undefined) {
      query.set('serviceDateTo', params.serviceDateTo);
    }

    if (params.status !== undefined) {
      query.set('status', params.status);
    }

    query.set('page', String(params.page));
    query.set('pageSize', String(params.pageSize));

    return apiRequest<DeliveryNoteSearchResult>(`/delivery-notes?${query.toString()}`, { signal: signal ?? null });
  },
  getNote: (id, signal) => apiRequest<DeliveryNote>(`/delivery-notes/${id}`, { signal: signal ?? null }),
  sign: (noteId, capture, signal) =>
    apiRequest<DeliveryNote>(`/delivery-notes/${noteId}/sign`, {
      method: 'POST',
      body: JSON.stringify(capture),
      signal: signal ?? null,
    }),
};

export const signatureQueue = createSignatureQueue({ store: getOfflineStore(), send: defaultApi.sign });

const repository = createDeliveriesRepository({
  store: getOfflineStore(),
  queue: signatureQueue,
  api: defaultApi,
  getUserId: currentUserId,
});

export const loadDeliveries = (serviceDate: string): Promise<DeliveriesSnapshot<DeliveryNote[]>> =>
  repository.loadDeliveries(serviceDate);
export const loadDelivery = (id: string): Promise<DeliveriesSnapshot<DeliveryNote>> =>
  repository.loadDelivery(id);
export const submitSignature = (noteId: string, capture: SignatureCapture): Promise<SubmitResult> =>
  repository.submitSignature(noteId, capture);
export const syncPending = (userId: string): Promise<SyncSummary> => repository.syncPending(userId);
